import styled from 'styled-components';

// Main container for pages
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Arial', sans-serif;
  direction: rtl;
  text-align: right;
`;

// Header component
export const Header = styled.header`
  text-align: center;
  padding: 40px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 15px;
  margin-bottom: 40px;
`;

// App logo placeholder
export const AppLogo = styled.div`
  width: 120px;
  height: 120px;
  background: #f0f0f0;
  border-radius: 20px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #666;
`;

// Main title
export const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 10px;
  font-weight: bold;
`;

// Subtitle
export const Subtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

// Section container
export const Section = styled.section`
  margin: 40px 0;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
`;

// Section title
export const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 2px solid #667eea;
  padding-bottom: 10px;
`;

// Form components
export const ContactForm = styled.form`
  display: grid;
  gap: 20px;
  max-width: 600px;
`;

export const Input = styled.input`
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea`
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

export const Button = styled.button`
  padding: 15px 30px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.3s;
  
  &:hover:not(:disabled) {
    background: #5a6fd8;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

// FAQ components
export const FAQItem = styled.div`
  margin: 20px 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`;

export const FAQQuestion = styled.div`
  padding: 20px;
  background: #f8f9fa;
  cursor: pointer;
  font-weight: bold;
  transition: background 0.3s;
  
  &:hover {
    background: #e9ecef;
  }
`;

export const FAQAnswer = styled.div`
  padding: 20px;
  background: white;
  border-top: 1px solid #e0e0e0;
  line-height: 1.6;
`;

// Gallery components
export const ScreenshotGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

export const ScreenshotPlaceholder = styled.div`
  height: 400px;
  background: #f0f0f0;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  border: 2px dashed #ccc;
  position: relative;
  overflow: hidden;
`;

export const ScreenshotImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 15px;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

export const ScreenshotContainer = styled.div`
  height: 400px;
  border-radius: 15px;
  position: relative;
  overflow: hidden;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

export const ScreenshotOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
  font-weight: bold;
  
  ${ScreenshotContainer}:hover & {
    opacity: 1;
  }
`;

export const ImageUploadArea = styled.label`
  height: 400px;
  border-radius: 15px;
  border: 2px dashed #ccc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9f9f9;
  
  &:hover {
    border-color: #667eea;
    background: #f0f4ff;
  }
  
  input[type="file"] {
    display: none;
  }
`;

export const UploadIcon = styled.div`
  font-size: 48px;
  color: #ccc;
  margin-bottom: 10px;
`;

export const UploadText = styled.p`
  color: #666;
  font-size: 14px;
  text-align: center;
  margin: 0;
`;

// Feature components
export const FeatureList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

export const FeatureCard = styled.div`
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border-right: 4px solid #667eea;
`;

// Footer
export const Footer = styled.footer`
  text-align: center;
  padding: 40px 0;
  background: #333;
  color: white;
  border-radius: 15px;
  margin-top: 40px;
`;

// Links
export const Link = styled.a`
  color: #667eea;
  text-decoration: none;
  margin: 0 15px;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Privacy Policy specific components
export const PolicyContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Arial', sans-serif;
  direction: rtl;
  text-align: right;
  line-height: 1.6;
`;

export const PolicyHeader = styled.header`
  text-align: center;
  padding: 30px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 15px;
  margin-bottom: 30px;
`;

export const PolicyTitle = styled.h1`
  font-size: 2.2rem;
  margin-bottom: 10px;
  font-weight: bold;
`;

export const EffectiveDate = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

export const PolicySection = styled.section`
  margin: 30px 0;
  padding: 25px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.1);
`;

export const PolicySectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: #333;
  border-bottom: 1px solid #667eea;
  padding-bottom: 8px;
`;

export const PolicySubTitle = styled.h3`
  font-size: 1.2rem;
  margin: 20px 0 10px 0;
  color: #555;
`;

export const PolicyText = styled.p`
  margin-bottom: 15px;
  color: #666;
  line-height: 1.7;
`;

export const PolicyList = styled.ul`
  margin: 15px 0;
  padding-right: 20px;
  
  li {
    margin-bottom: 8px;
    color: #666;
  }
`;

export const HighlightBox = styled.div`
  background: #f8f9fa;
  border-right: 4px solid #667eea;
  padding: 20px;
  margin: 20px 0;
  border-radius: 5px;
`;

export const ContactInfo = styled.div`
  background: #e3f2fd;
  padding: 20px;
  border-radius: 10px;
  margin: 20px 0;
  border: 1px solid #bbdefb;
`;

export const TableOfContents = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin: 20px 0;
  border: 1px solid #e0e0e0;
`;

export const TOCLink = styled.a`
  display: block;
  color: #667eea;
  text-decoration: none;
  padding: 5px 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const LogoImage = styled.img`
  width: 8rem;
  height: 8rem;
  object-fit: cover;
  border-radius: 1rem;
  margin-bottom: 1.5rem;
`;